import { useId, useState } from 'react'
import {
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
  HeartOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Input } from '../../../components/ui/Input'
import { Spinner } from '../../../components/ui/Spinner'
import { Tabs } from '../../../components/ui/Tabs'
import { useToast } from '../../../components/ui/ToastProvider'
import { cx } from '../../../components/ui/cx'
import { useCartCount } from '../../../lib/cart/cart-count-context'
import { QuantityStepper } from '../../cart/QuantityStepper'
import { addCatalogResultToCart } from '../catalog-add-to-cart'
import {
  type ConfigDimensionSegment,
  type ConfigSchema,
  type SkuPicks,
  computeRangePresets,
  formatValueToken,
  gateConfigSchema,
  isValueLegal,
  validateAndComposeSku,
} from '../configurator-sku'
import { resultDisplayName } from '../format'
import type {
  ConfigurableResolveService,
  ConfigurableResolveState,
} from '../useConfigurableResolve'
import { useConfigurableResolve } from '../useConfigurableResolve'
import { useRecordConfiguratorView } from '../useRecentlyViewed'
import { CatalogResultThumb } from './CatalogResultThumb'

export interface ConfiguratorCoreProps {
  /** The family/card display name, shown in the header alongside the
   * dimension's field name (when known). */
  readonly familyName: string
  readonly schema: ConfigSchema
  /**
   * The family's real `sourceable.imageRef` (catalog-series-row-expand-
   * visual-fidelity pass) — both callers (`CatalogSeriesConfigurePage`'s
   * Surface A row-expand and `ConfiguratorDrawer`'s Surface B PDP drawer)
   * always have a real `sourceable` with this field, so it's threaded
   * through rather than fabricated. Renders `CatalogResultThumb`'s
   * established real-image/honest-placeholder treatment (never a stub) —
   * matching the design's row-expand mock, which always shows a 120×120
   * image next to the family name. Left `undefined` (the prop simply
   * omitted) renders NO thumbnail at all, distinct from `null` ("we asked
   * for the image, there isn't one" — the placeholder) — existing tests
   * that don't care about the thumbnail keep seeing exactly what they saw
   * before this prop existed.
   */
  readonly imageRef?: string | null
  /** Injectable for tests; defaults to the real BFF-backed resolve. */
  readonly resolveService?: ConfigurableResolveService
  /** Called after a successful add (e.g. the drawer chrome closes itself). */
  readonly onAdded?: () => void
}

/**
 * ⚠️ PLACEHOLDER / DEMO CHROME — NOT REAL DATA. ⚠️
 *
 * Added 2026-08-26 at the user's explicit request for a full local visual
 * mock of the reference design's per-series card, to review BEFORE deciding
 * whether/how to commit anything. These three blocks (Series Specifications,
 * the 3D Preview/CAD/favorite action row, and Volume pricing) are static,
 * presentational chrome with zero backing in `catalog_item`/`configAttributes`
 * (confirmed absent — ADR-0099's data audit, re-checked against the current,
 * larger seed). None of it is wired to pricing, `resolveConfigurable`, or the
 * cart. Unlike this chrome, the dimension TABS below are 100% real — every
 * tab comes from `schema.segments`, never a fabricated option list; there is
 * no separate "mock dimension" concept in this component.
 *
 * ADR-0090/0091 ("real data authoritative; omit/disable, don't fabricate")
 * forbid shipping this chrome as real functionality — satisfied here by
 * making it strict placeholders: every action button below is `disabled`
 * (not just handler-less), so nothing reads as live functionality that
 * silently does nothing on click. **Product decision recorded (2026-08-26,
 * demo-site scope, UID-338): keep this chrome as permanent decorative UI
 * with no real selection semantics**, satisfying condition (b) below —
 * this is not scaffolding awaiting removal. If a real facet-ingestion
 * source lands later (UID-320/UID-321), replace the mock constants with
 * real data rather than re-deciding whether to show the chrome at all.
 */
const MOCK_SERIES_SPECS: ReadonlyArray<readonly [string, string]> = [
  ['Shaft End Perpendicularity', 'Perpendicularity (0.2)'],
  ['Shaft Tolerance', 'h5 ~ f8'],
  ['Tightening Tool Type', 'None'],
  ['Left End Shape', 'No Machining'],
  ['Material', 'Steel / Stainless Steel'],
  ['Right End Shape', 'No Machining'],
]

interface MockVolumeTier {
  readonly range: string
  readonly minQty: number
  readonly discount: number
}

const MOCK_VOLUME_TIERS: readonly MockVolumeTier[] = [
  { range: '1–4', minQty: 1, discount: 0 },
  { range: '5–19', minQty: 5, discount: 0.28 },
  { range: '20–49', minQty: 20, discount: 0.37 },
  { range: '50–99', minQty: 50, discount: 0.45 },
  { range: '100–149', minQty: 100, discount: 0.5 },
  { range: '150+', minQty: 150, discount: 0.56 },
]

type DimensionSegment = Extract<ConfigDimensionSegment, { kind: 'range' | 'enum' }>

interface Tab {
  readonly id: string
  readonly label: string
  readonly index: number
  readonly segment: DimensionSegment
}

/** The outer "Drawing / Specs · CAD preview · Part number" tab strip
 * (2026-09-03 legacy-mock parity pass) — three FIXED tabs, always present
 * regardless of `schema`, unlike {@link buildTabs}'s per-dimension chips
 * below. `'drawing-specs'` is the default/first tab and wraps every real
 * field this component already rendered before this pass (series specs,
 * dimension picker, status row) — nothing about that content changes, only
 * its container. */
const OUTER_TABS = [
  { id: 'drawing-specs', label: 'Drawing / Specs' },
  { id: 'cad-preview', label: 'CAD preview' },
  { id: 'part-number', label: 'Part number' },
] as const

/**
 * "Part Number: <resolved SKU>" once every dimension resolves, or the
 * legacy mock's own placeholder copy before that — ALWAYS renders the
 * label, never omits the line pre-resolution (the gap this pass fixes).
 * Shared by the header (always visible) and the "Part number" tab panel
 * (2026-09-03) so both read from the same one `resolveState`, never two
 * independent conditions that could drift.
 */
function PartNumberLine({ resolveState }: { readonly resolveState: ConfigurableResolveState }) {
  const resolvedSku = resolveState.status === 'resolved' ? resolveState.sourceable.catalogSku : null
  return (
    <p className="configurator-core-pn-line">
      <span className="configurator-core-pn-label">Part Number: </span>
      {resolvedSku ? (
        <span className="configurator-core-pn">{resolvedSku}</span>
      ) : (
        <span className="configurator-core-pn-placeholder">
          Please configure to get your part number
        </span>
      )}
    </p>
  )
}

/** Real dimension tabs only — one per `range`/`enum` segment on `schema`, in
 * template order. A family with one real dimension (the common case) gets
 * one tab; a family with none isn't reachable here (`gate.kind==='disabled'`
 * returns before this runs). No fabricated tabs are ever added. */
function buildTabs(schema: ConfigSchema): Tab[] {
  return schema.segments
    .map((segment, index) => ({ segment, index }))
    .filter(
      (entry): entry is { segment: DimensionSegment; index: number } =>
        entry.segment.kind === 'range' || entry.segment.kind === 'enum',
    )
    .map(({ segment, index }) => ({
      id: `dim-${index}`,
      label: segment.fieldName ?? 'Value',
      index,
      segment,
    }))
}

/** A tab is "finished" once its picked value is BOTH present AND legal for
 * its own segment — the same rule `allDimensionsSet` uses (a typed-but-out-
 * of-range value in `picks` must not count). */
function isTabFinished(tab: Tab, picks: SkuPicks): boolean {
  const value = picks.get(tab.index)
  return value !== undefined && isValueLegal(tab.segment, value)
}

/**
 * "Auto-advance to the next unfinished spec" (2026-09-10 UX fix) — after a
 * value is committed for `currentTabId`, find the next tab (in template
 * order, wrapping around) that has no legal value yet. Returns `null` when
 * every tab is finished (or `currentTabId` isn't a real tab), in which case
 * the caller leaves the active tab exactly where it was — never forces a
 * jump back to index 0.
 */
function findNextUnfinishedTab(
  tabs: readonly Tab[],
  picks: SkuPicks,
  currentTabId: string,
): Tab | null {
  const currentPos = tabs.findIndex((tab) => tab.id === currentTabId)
  if (currentPos === -1) return null
  for (let offset = 1; offset <= tabs.length; offset++) {
    const tab = tabs[(currentPos + offset) % tabs.length] as Tab
    if (!isTabFinished(tab, picks)) return tab
  }
  return null
}

/**
 * The configurator core shared by Surface A (series → table row-expand,
 * `CatalogSeriesConfigurePage`) and Surface B (result-card → PDP drawer,
 * `ConfiguratorDrawer`) — ADR-0097 Decision 1 / ADR-0098 Decision 2: one
 * state machine over one family's N ordered dimension segments, only the
 * surrounding chrome differs.
 *
 * Renders one tab per REAL dimension segment (see {@link buildTabs}), with a
 * single active tab's options shown below the tab row at a time. A picked
 * tab collapses into a "label: value" chip; clicking any chip re-opens it.
 * Real dimensions are governed by {@link validateAndComposeSku} /
 * {@link isValueLegal} exactly as before — a picked value can only ever be
 * one the picker itself offered (enum) or one that passed the range's own
 * legality check — so a client-fabricated price or a silently-accepted
 * illegal value stays unrepresentable. Add-to-Cart stays disabled until the
 * composed SKU resolves.
 *
 * 2026-09-03 legacy-mock parity pass — the main column is now organized
 * under the legacy mock's own three-tab strip ("Drawing / Specs" · "CAD
 * preview" · "Part number", see {@link OUTER_TABS}) rather than one
 * unconditional stack: "Drawing / Specs" carries every field this component
 * already rendered (unchanged), "CAD preview" is an honest placeholder (no
 * real CAD asset field exists on the contract), and "Part number" surfaces
 * the SAME {@link PartNumberLine} the header shows. Add-to-Cart's
 * `resolveState.status === 'resolved'` gate — already correct before this
 * pass — is untouched.
 */
export function ConfiguratorCore({
  familyName,
  schema,
  imageRef,
  resolveService,
  onAdded,
}: ConfiguratorCoreProps) {
  const gate = gateConfigSchema(schema)
  const tabs = buildTabs(schema)

  const [picks, setPicks] = useState<SkuPicks>(new Map())
  const [rawInputs, setRawInputs] = useState<ReadonlyMap<number, string>>(new Map())
  const [activeTabId, setActiveTabId] = useState<string | null>(tabs[0]?.id ?? null)
  const [activeOuterTabId, setActiveOuterTabId] = useState<string>(OUTER_TABS[0].id)
  const [quantity, setQuantity] = useState(1)
  const inputIdBase = useId()
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const [adding, setAdding] = useState(false)

  const sku = validateAndComposeSku(schema, picks)
  const resolveState = useConfigurableResolve(sku, resolveService)
  // Client-only "Recently viewed" tracking, V1 capture (debounced — a
  // dimension-pill scrub emits many resolves; only the SKU that settles
  // gets recorded). Shared by both configurator surfaces (this core backs
  // Surface A's row-expand AND Surface B's `ConfiguratorDrawer`), so one
  // call site here covers both.
  useRecordConfiguratorView(resolveState)

  const header = (
    <div className="configurator-core-header">
      {imageRef !== undefined && (
        <CatalogResultThumb
          imageRef={imageRef}
          name={familyName}
          className="configurator-core-thumb"
        />
      )}
      <div className="configurator-core-heading">
        <p className="configurator-core-name">{familyName}</p>
        <div className="configurator-core-links">
          <PartNumberLine resolveState={resolveState} />
          {/* Placeholder-only links (see the demo-chrome caveat above) — no
              target, never navigate anywhere; purely visual parity with the
              design. */}
          <button type="button" className="configurator-core-link-button">
            <FileGlyph />
            Catalog Page
          </button>
          <button type="button" className="configurator-core-link-button">
            <SparkleGlyph />
            Tech Specs
          </button>
        </div>
        <p className="configurator-core-description">
          Representative configuration for {familyName}. Set quantity and add to cart, or download
          CAD.
        </p>
      </div>
    </div>
  )

  if (gate.kind === 'disabled') {
    return (
      <div className="configurator-core configurator-core-disabled">
        {header}
        <p className="configurator-core-disabled-note">
          Full configuration for this family isn’t available yet.
        </p>
      </div>
    )
  }

  function pickValue(segmentIndex: number, value: number | null) {
    setPicks((prev) => {
      const next = new Map(prev)
      if (value === null) {
        next.delete(segmentIndex)
      } else {
        next.set(segmentIndex, value)
      }
      return next
    })
  }

  /** Commits `value` for `tab` (same semantics as {@link pickValue}) and, if
   * that commit leaves `tab` finished, advances the active tab to the next
   * unfinished spec — the auto-advance behavior for chip/pill selection,
   * where a single click IS the commit. Returns the up-to-date picks map so
   * callers that also need the freshly-committed value (none currently do)
   * don't have to re-derive it from stale state. */
  function commitPickAndAdvance(tab: Tab, value: number | null): SkuPicks {
    const next = new Map(picks)
    if (value === null) {
      next.delete(tab.index)
    } else {
      next.set(tab.index, value)
    }
    setPicks(next)
    advanceIfFinished(tab, next)
    return next
  }

  /** Shared by both the chip/pill commit path and the free-text input's
   * blur-commit path (see `configurator-core-tab-value` onBlur below) — if
   * `tab` is finished under `picksAfterCommit`, move to the next unfinished
   * spec; if every spec is finished, no-op and stay put (never force a jump
   * to tab index 0). */
  function advanceIfFinished(tab: Tab, picksAfterCommit: SkuPicks) {
    if (!isTabFinished(tab, picksAfterCommit)) return
    const next = findNextUnfinishedTab(tabs, picksAfterCommit, tab.id)
    if (next) setActiveTabId(next.id)
  }

  async function handleAdd() {
    if (resolveState.status !== 'resolved') return
    const { sourceable } = resolveState
    setAdding(true)
    try {
      const result = await addCatalogResultToCart(sourceable, quantity)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${resultDisplayName(sourceable)} to cart`)
      onAdded?.()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    } finally {
      setAdding(false)
    }
  }

  const canAdd = resolveState.status === 'resolved'
  const resolvedPrice =
    resolveState.status === 'resolved' ? resolveState.sourceable.indicativePrice : undefined
  const unitPrice = resolvedPrice ? Number(resolvedPrice.amount) : null

  const activeTab = tabs.find((tab) => tab.id === activeTabId)

  // "All dimensions set" confirmation (per the reference mock) — every real
  // dimension tab must have a value that is BOTH present AND legal for its
  // own segment; a typed-but-out-of-range/off-step value in `picks` (the
  // Input's live error state) must not count as "set".
  const allDimensionsSet =
    tabs.length > 0 &&
    tabs.every((tab) => {
      const value = picks.get(tab.index)
      return value !== undefined && isValueLegal(tab.segment, value)
    })

  const drawingSpecsPanel = (
    <>
      <p className="configurator-core-series-specs-heading">Series Specifications</p>
      <dl className="configurator-core-series-specs">
        {MOCK_SERIES_SPECS.map(([label, value]) => (
          <div key={label} className="configurator-core-series-spec-row">
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <div className="configurator-core-tabs">
        {tabs.map((tab) => {
          const pickedValue = picks.get(tab.index)
          // Format through `formatValueToken` (rounds to the segment's own
          // step precision) rather than a raw `toString()` — a picked value
          // computed from `min + stepIndex * step` can carry binary-float
          // noise (`10.799999999999999`) that must never reach the UI.
          const value =
            pickedValue !== undefined
              ? formatValueToken(tab.segment, pickedValue)
              : rawInputs.get(tab.index)
          const isSet = picks.has(tab.index)
          const isActive = activeTabId === tab.id

          return (
            <button
              key={tab.id}
              type="button"
              aria-pressed={isActive}
              className={cx(
                'configurator-core-tab',
                isActive && 'configurator-core-tab-active',
                isSet && 'configurator-core-tab-set',
              )}
              onClick={() => setActiveTabId(tab.id)}
            >
              {isSet ? (
                <>
                  <span className="configurator-core-tab-label">{tab.label}</span>
                  <span className="configurator-core-tab-value">{value}</span>
                  <EditOutlined aria-hidden="true" />
                </>
              ) : (
                tab.label
              )}
            </button>
          )
        })}
      </div>

      {allDimensionsSet && (
        <p className="configurator-core-all-set">
          <CheckOutlined aria-hidden="true" /> All dimensions set — ready to add. Tap any chip above
          to change.
        </p>
      )}

      {activeTab && (
        <div className="configurator-core-tab-panel">
          <p className="configurator-core-tab-panel-heading">Select {activeTab.label}</p>

          {activeTab.segment.kind === 'enum' && (
            <div className="configurator-core-pills">
              {activeTab.segment.values.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={cx(
                    'configurator-core-pill',
                    picks.get(activeTab.index) === value && 'configurator-core-pill-selected',
                  )}
                  aria-pressed={picks.get(activeTab.index) === value}
                  onClick={() => commitPickAndAdvance(activeTab, value)}
                >
                  {value}
                </button>
              ))}
            </div>
          )}

          {activeTab.segment.kind === 'range' &&
            (() => {
              const segment = activeTab.segment
              const presets = computeRangePresets(segment)
              const pickedValue = picks.get(activeTab.index) ?? null

              return (
                <div className="configurator-core-range-picker">
                  <div className="configurator-core-pills">
                    {presets.map((value) => {
                      const selected = pickedValue === value
                      return (
                        <button
                          key={value}
                          type="button"
                          className={cx(
                            'configurator-core-pill',
                            selected && 'configurator-core-pill-selected',
                          )}
                          aria-pressed={selected}
                          onClick={() => {
                            const formatted = formatValueToken(segment, value)
                            setRawInputs((prev) => new Map(prev).set(activeTab.index, formatted))
                            commitPickAndAdvance(activeTab, value)
                          }}
                        >
                          {formatValueToken(segment, value)}
                        </button>
                      )
                    })}
                  </div>
                  <Input
                    label={activeTab.label}
                    hideLabel
                    className="configurator-core-custom-input"
                    id={`${inputIdBase}-${activeTab.index}`}
                    type="number"
                    inputMode="decimal"
                    min={segment.min}
                    max={segment.max}
                    step={segment.step}
                    placeholder={`${formatValueToken(segment, segment.min)}–${formatValueToken(segment, segment.max)}`}
                    value={rawInputs.get(activeTab.index) ?? ''}
                    error={
                      pickedValue !== null && !isValueLegal(segment, pickedValue)
                        ? `Enter a value between ${segment.min} and ${segment.max}`
                        : undefined
                    }
                    onChange={(event) => {
                      const text = event.target.value
                      setRawInputs((prev) => new Map(prev).set(activeTab.index, text))
                      const parsed = text.trim() === '' ? null : Number(text)
                      pickValue(
                        activeTab.index,
                        parsed !== null && Number.isFinite(parsed) ? parsed : null,
                      )
                    }}
                    // Auto-advance on blur, not on every keystroke — `onChange`
                    // above already commits live (needed for the inline error
                    // state as the user types), but treating every keystroke
                    // as "entry complete" would yank focus away mid-type.
                    // Blur is this input's actual commit trigger.
                    onBlur={() => advanceIfFinished(activeTab, picks)}
                  />
                </div>
              )
            })()}
        </div>
      )}
    </>
  )

  return (
    <div className="configurator-core">
      <div className="configurator-core-main">
        {header}

        <Tabs
          items={OUTER_TABS}
          activeId={activeOuterTabId}
          onChange={setActiveOuterTabId}
          aria-label="Configurator details"
          className="configurator-core-outer-tabs"
        >
          {(tabId) => {
            if (tabId === 'cad-preview') {
              // 2026-09-03 legacy-mock parity pass — the tab itself matches
              // the legacy layout (Drawing/Specs · CAD preview · Part
              // number), but there is NO real CAD asset field on the
              // resolved/pre-resolve `Sourceable` to show here (confirmed
              // absent from the contract, same audit basis as the
              // `MOCK_SERIES_SPECS` caveat above) — an honest empty state,
              // never a fabricated preview.
              return (
                <EmptyState
                  title="CAD preview isn’t available yet"
                  detail="We don’t have a CAD asset on file for this item."
                />
              )
            }
            if (tabId === 'part-number') {
              return (
                <div className="configurator-core-tab-panel">
                  <p className="configurator-core-tab-panel-heading">Part Number</p>
                  <PartNumberLine resolveState={resolveState} />
                </div>
              )
            }
            return drawingSpecsPanel
          }}
        </Tabs>

        <div className="configurator-core-status-row">
          {resolveState.status === 'loading' && (
            <span className="configurator-core-status">
              <Spinner size="sm" aria-hidden="true" />
              Checking availability…
            </span>
          )}
          {resolveState.status === 'not-available' && (
            <p className="configurator-core-status configurator-core-status-warning">
              Not available at this value.
            </p>
          )}
          {resolveState.status === 'error' && (
            <p className="configurator-core-status configurator-core-status-error">
              {resolveState.message}
            </p>
          )}
          {resolveState.status === 'idle' && (
            <span className="configurator-core-status">Configure to add</span>
          )}
        </div>
      </div>

      <div className="configurator-core-panel">
        {/* Placeholder-only actions (see the demo-chrome caveat above) — no
            target, never do anything; purely visual parity with the design.
            Explicitly `disabled` (not just handler-less) so they read as
            inert rather than silently-broken — a real product decision to
            keep this as decorative demo UI, not a stub awaiting wiring. */}
        <div className="configurator-core-action-row">
          <button type="button" className="configurator-core-action-button" disabled>
            3D Preview
          </button>
          <button type="button" className="configurator-core-action-button" disabled>
            <DownloadOutlined aria-hidden="true" />
            CAD
          </button>
          <button
            type="button"
            className="configurator-core-action-button configurator-core-favorite-button"
            aria-label="Save for later (not available in this demo)"
            disabled
          >
            <HeartOutlined aria-hidden="true" />
          </button>
        </div>

        <div className="configurator-core-price-panel">
          <div className="configurator-core-price-row">
            <span className="configurator-core-price-label">Unit Price</span>
            {resolvedPrice && (
              <p className="configurator-core-price">
                {resolvedPrice.currency} {resolvedPrice.amount}
              </p>
            )}
          </div>

          {/* Ship Date is NOT real — `GET /catalog/configurable/{sku}` resolves
              to a bare Sourceable with no lead-time field, unlike the
              pre-resolve family card's `availability`. Static placeholder
              text only, per the demo-chrome caveat above. */}
          <div className="configurator-core-price-row">
            <span className="configurator-core-price-label">Ship Date</span>
            <span className="configurator-core-ship-date">Ships in 1 day~</span>
          </div>

          <QuantityStepper label="Qty." value={quantity} onChange={setQuantity} />

          <Button
            type="button"
            variant="primary"
            disabled={!canAdd}
            loading={adding}
            onClick={() => void handleAdd()}
          >
            Add to Cart
          </Button>
        </div>

        <div className="configurator-core-volume-pricing">
          <div className="configurator-core-volume-pricing-header">
            <span>Volume pricing</span>
            <span className="configurator-core-volume-pricing-subheader">unit price by qty</span>
          </div>
          <div className="configurator-core-volume-pricing-columns">
            <span>QTY</span>
            <span>UNIT</span>
            <span>SAVE</span>
          </div>
          {MOCK_VOLUME_TIERS.map((tier, tierIndex) => {
            const isActiveTier =
              quantity >= tier.minQty &&
              (MOCK_VOLUME_TIERS[tierIndex + 1] === undefined ||
                quantity < (MOCK_VOLUME_TIERS[tierIndex + 1]?.minQty ?? Number.POSITIVE_INFINITY))
            const tierPrice = unitPrice != null ? unitPrice * (1 - tier.discount) : null
            return (
              <div
                key={tier.range}
                className={cx(
                  'configurator-core-volume-pricing-row',
                  isActiveTier && 'configurator-core-volume-pricing-row-active',
                )}
              >
                <span>{tier.range}</span>
                <span>{tierPrice != null ? `$${tierPrice.toFixed(2)}` : '—'}</span>
                <span>{tier.discount > 0 ? `−${Math.round(tier.discount * 100)}%` : '—'}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** Minimal inline glyphs for the placeholder "Catalog Page"/"Tech Specs"
 * links — not worth pulling in two more `@ant-design/icons` re-exports for
 * chrome this provisional; see the demo-chrome caveat above. */
function FileGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 1.5h5l3 3v10h-8v-13Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function SparkleGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 1.5 9.5 6 14 7.5 9.5 9l-1.5 4.5L6.5 9 2 7.5 6.5 6z" fill="currentColor" />
    </svg>
  )
}

/** Re-exported so callers/tests can narrow a segment without re-importing
 * from `configurator-sku` directly. */
export type { ConfigDimensionSegment }
